import { AlertTriangle, ListChecks } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScoreComparisonBar } from '~/components/common/score-comparison-bar';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type { CoverageAttributeRow } from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

interface Props {
  rows: CoverageAttributeRow[];
}

/** Clickable colour key for the before/after coverage bars. Clicking solos its
 *  series (shows only that bar); an inactive item dims to signal it's filtered. */
function CoverageLegendItem({
  color,
  label,
  active,
  onClick,
  testId,
}: {
  color: 'before' | 'after';
  label: string;
  /** Whether this series is currently shown. */
  active: boolean;
  onClick: () => void;
  /** Automation hook for the legend toggle. */
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex cursor-pointer items-center gap-1.5 transition-opacity',
        active ? 'opacity-100' : 'opacity-40',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-3 w-3 shrink-0 rounded-full',
          color === 'before' ? 'bg-warning' : 'bg-primary',
        )}
      />
      <span className="text-text-date-pill text-xs">{label}</span>
    </button>
  );
}

/** Coverage by product attribute — the field checklist, before vs after.
 *  Two stacked bars per field rather than one, because the point of this card
 *  is the lift; a single "after" bar hides whether the merchant already had
 *  the value. Coverage is monotone upstream, so a shrinking row is flagged as
 *  suspect data rather than drawn as a normal regression. */
export function CoverageAttributeSignalList({ rows }: Props) {
  const { t } = useTranslation('catalog-operations');
  // Legend filter: null shows both bars (default); 'before'/'after' solos one.
  const [soloSeries, setSoloSeries] = useState<'before' | 'after' | null>(null);
  const beforeVisible = soloSeries !== 'after';
  const afterVisible = soloSeries !== 'before';

  return (
    <Card data-testid="batch-detail-attribute-signal-card">
      <CardHeader className="flex flex-row items-start gap-4 p-6">
        <div className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          <ListChecks className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="w-full space-y-2">
          <div className="space-y-0.5">
            <h2 data-testid="batch-detail-attribute-signal-title" className="text-xl font-medium">
              {t('coverageSignals.byAttribute.title')}
            </h2>
            <p className="text-muted-foreground text-sm font-light">
              {t('coverageSignals.byAttribute.subtitle')}
            </p>
          </div>
          {/* Legend sits on its own row under the copy, right-aligned: in a
              half-width grid card the title and this before/after key can't
              share a line, so stacking keeps the title/subtitle from being
              squeezed narrow. Clicking an item solos that bar; both show by
              default, and clicking the active one returns to both. */}
          <div className="flex items-center justify-end gap-3">
            <CoverageLegendItem
              color="before"
              testId="batch-detail-attribute-signal-legend-before-toggle"
              label={t('performanceSignals.legend.before')}
              active={beforeVisible}
              onClick={() => {
                setSoloSeries((prev) => (prev === 'before' ? null : 'before'));
              }}
            />
            <CoverageLegendItem
              color="after"
              testId="batch-detail-attribute-signal-legend-after-toggle"
              label={t('performanceSignals.legend.after')}
              active={afterVisible}
              onClick={() => {
                setSoloSeries((prev) => (prev === 'after' ? null : 'after'));
              }}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="bg-primary/4 mr-6 mb-6 ml-6 max-h-65 overflow-y-auto rounded-xl p-3">
        {rows.length === 0 ? (
          <p
            data-testid="batch-detail-attribute-signal-empty"
            className="text-muted-foreground px-3 py-6 text-center text-sm"
          >
            {t('coverageSignals.empty')}
          </p>
        ) : (
          <ul data-testid="batch-detail-attribute-signal-list" className="space-y-3">
            {rows.map((row) => (
              <li
                key={row.label}
                data-testid={`batch-detail-attribute-signal-item-${row.label}`}
                className="flex items-center gap-4 px-3 py-1"
              >
                {/* Attribute label — kept on the left so the row reads inline,
                    label · bars · counts, matching the score lists elsewhere. */}
                <span className="flex flex-1 items-center gap-1.5 text-sm">
                  {row.label}
                  {row.isRegression && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertTriangle
                          data-testid={`batch-detail-attribute-signal-regression-badge-${row.label}`}
                          className="text-destructive h-3.5 w-3.5 shrink-0"
                          aria-label={t('coverageSignals.byAttribute.regressionLabel')}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        {t('coverageSignals.byAttribute.regressionTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </span>
                {/* Before/after bars with inline product counts + lift chip. Bar
                    length is the coverage percent (0–100); the legend above is the
                    colour key and solos a series via `showBefore`/`showAfter`. */}
                <div className="w-full max-w-[80%] shrink-0">
                  <ScoreComparisonBar
                    beforeScore={row.beforePercent}
                    afterScore={row.afterPercent}
                    maxScore={100}
                    hideLabels
                    beforeCount={row.beforeCount}
                    afterCount={row.afterCount}
                    showBefore={beforeVisible}
                    showAfter={afterVisible}
                    testId={`batch-detail-attribute-signal-score-${row.label}`}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
